import React from 'react';

import { ThemeProvider, styled } from 'styled-components';
import { theme } from '../theme/aikya';

import { Header }  from './Header';
import { HeroParagraph, Paragraph, ParagraphSmall, UnorderedList, UnorderedListItem } from '../theme/typography';
import { Button  } from './Button';
import { TextLink } from './TextLink';
import { PageTitle } from './PageTitle';
import { StandardSectionContentContainer, StandardSection } from './StandardSection';
import { ContactBlock } from './ContactBlock';
import { ServalActionItemBlock } from './ServalActionItemBlock';
import { Breadcrumbs, BreadcrumbChevron, BreadcrumbLink } from './Breadcrumbs';
import { PageWrapper } from './PageWrapper';
import { FooterSection } from './FooterSection';

import BlackfootHero from '../images/blackfoot/blackfoot-hero.jpg';
import BlackfootFea from '../images/blackfoot/blackfoot-fea.jpg'

const StyledHeroImage = styled.img`
	border-radius: 0.25rem;
	width: 100%;
`;

const ButtonContainer = styled.div`
	align-items: stretch;
	margin-bottom: 2rem;
	margin-top: 2rem;
	max-width: 32rem;
	display: flex;
	gap: 1rem;

	@media only screen and (min-width: ${(props) => props.theme.breakpoints.first}) {
		display: flex;
	}
`;

const TextContainer = styled.div`
	max-width: 32rem;
`;

const TopContainer = styled.div`
	max-width: 48rem;
`;

const Heading = styled.h3`
	${(props) => props.theme.typography.headingS};
	margin-bottom: 1rem;
`;

const Aside = styled(ParagraphSmall)`
	color: ${(props) => props.theme.colors.neutral4};
	border-left: 1px solid ${(props) => props.theme.colors.neutral6};
	padding-left: 1rem;

	@media only screen and (min-width: ${(props) => props.theme.breakpoints.first}) {
		padding-left: 1rem;
	}
`;

export class BlackfootPage extends React.Component {

    render() {

        return (
			<ThemeProvider theme={theme}>
				<PageWrapper>
					<Header />
					<StandardSection>
						<StandardSectionContentContainer>
							<TopContainer>
								<Breadcrumbs>
										<BreadcrumbLink to="/frames">
											Frames
										</BreadcrumbLink>
										<BreadcrumbChevron>&gt;</BreadcrumbChevron>
								</Breadcrumbs>
								<PageTitle>Blackfoot</PageTitle>
								<HeroParagraph>A hilariously tiny, 3D-printable drone frame for extreme agility, stealth and cuteness.</HeroParagraph>
								<HeroParagraph>Gold medalist of the 2025 <TextLink link="https://tinydrones.hu" label="Tiny Drones Hungary" /> Build Competition.</HeroParagraph>
								<ButtonContainer>
									<Button 
										as="a"
										colorContext="light"
										href={`https://www.printables.com/model/1758668-tressym-blackfoot-ultralight-fpv-drone-frame`}
										label="Download from Printables"
										download
									/>
									<Button 
										as="a"
										colorContext="light"
										href={`https://makerworld.com/en/models/2951470-tressym-blackfoot-ultralight-fpv-drone-frame`}
										label="Download from Makerworld"
										download
										variant="outlined"
									/>
								</ButtonContainer>
							</TopContainer>
							<StyledHeroImage src={BlackfootHero} alt="Blackfoot drone frame" />
							<TextContainer>
								<Heading>About this frame</Heading>
								<Paragraph>Every once in a while, someone has the idea to try a 3D-printed drone frame, only to then discover how easily they break, and how bad they are at damping vibrations. But what if the frame was <em>really fucking tiny,</em> so that there's no weight to actually break anything in a crash?
								</Paragraph>
								<Paragraph>The Blackfoot frame weighs 1.69 grams, which is two-thirds the weight of the "NBD Cockroach LiteAF" ultralight 65mm racing frame. The drone I built on it had a dry weight of 15.64 grams. It easily beats tinywhoops in agility and flight time, and it's super cute and silent.</Paragraph>
								<StyledHeroImage src={BlackfootFea} alt="Blackfoot drone frame finite element analysis" />
								<Paragraph>And even though the frame is incredibly light, it holds up pretty well. I used the same techniques to strengthen it that I used on my <TextLink link="https://tressym.eu/frames/serval" label="Serval 5-inch racing frame" />. By fine-tuning the shape of the arms, I was able to increase their durability by more than 200% compared to the first version.</Paragraph>
								<Heading>How to print</Heading>
								<Paragraph>For this frame to work well, you need to print it from a flexible, sturdy material, such as PETG. PLA won't work as well, because it's very brittle.</Paragraph>
								<Paragraph>Don't use a CF infused filament. They aren't stronger and they're toxic.</Paragraph>
								<Aside>CF filaments only contain tiny, microscopic fibers that don't seem to make the printed parts stronger - in fact, the tests I saw found that they're weaker. At the same time, those little fibers can easily get into your lungs and really fuck them up.</Aside>
								<Paragraph>Print upside-down. Use supports if your printer struggles with the overhangs on the battery holder.</Paragraph>
								<Heading>How to build</Heading>
								<Paragraph>The Blackfoot is designed for 31mm props - the ones that are used on 65mm tinywhoops. It pairs perfectly with my <TextLink link="https://tressym.eu/parts/joker" label="ultralight (0.76g) Joker canopy" />. If you use another canopy, you might need to adjust the prop clearances. In terms of AIOs, motors and other parts, just slap on whichever 65mm tinywhoop parts you have and they should work fine.</Paragraph>
								<Paragraph>Besides the frame, you'll also need:</Paragraph>
								<UnorderedList>
									<UnorderedListItem><Paragraph>4x M2x8mm screws</Paragraph></UnorderedListItem>
									<UnorderedListItem><Paragraph>4x M2 nuts</Paragraph></UnorderedListItem>
								</UnorderedList>
								<Paragraph>I recommend plastic for both.</Paragraph>
								<Heading>How to modify</Heading>
								<Paragraph>The Blackfoot is open source. Feel free to use or remix it, just link back to this page when you do! I'm releasing it under the <TextLink link="https://creativecommons.org/licenses/by-sa/4.0/" label="Creative Commons-Attribution-Share Alike" /> license. You can find the <TextLink link="https://cad.onshape.com/documents/36d8ca2504976c3561e21523/w/e713571db2192850c0fad1bd/e/cfe06c60423fbfe5aad6b271?renderMode=0&rightPanel=variableTablePanel&uiState=6a3533dbb5481248ec19db49" label="editable Blackfoot files" /> on Onshape. I built this design with variables, so it should be super easy to modify most things.</Paragraph>
								<ButtonContainer>
									<Button 
										as="a"
										colorContext="light"
										href={`https://www.printables.com/model/1758668-tressym-blackfoot-ultralight-fpv-drone-frame`}
										label="Download from Printables"
										download
									/>
									<Button 
										as="a"
										colorContext="light"
										href={`https://makerworld.com/en/models/2951470-tressym-blackfoot-ultralight-fpv-drone-frame`}
										label="Download from Makerworld"
										download
										variant="outlined"
									/>
								</ButtonContainer>
							</TextContainer>
						</StandardSectionContentContainer>
					</StandardSection>
					<ServalActionItemBlock />
					<ContactBlock />
					<FooterSection />
				</PageWrapper>
			</ThemeProvider>
		)
	}
}